import { useMemo, useState, useCallback } from "react";

/**
 * Compute the parent→children layout and the final filtered/collapsed view of the
 * task list. Everything about the task tree lives here so ProjectView stays small.
 *
 * Returns:
 *   orderedTasks        — parents in order, each immediately followed by its children
 *   childrenByParent    — Map<parentId, Task[]>
 *   filteredTasks       — after search + workstream + status + collapse filters
 *   collapsed / toggleCollapse — per-parent expand/collapse state
 */
export default function useTaskHierarchy(tasks, { search, workstreamFilter, statusFilter } = {}) {
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = useCallback(
    (parentId) => setCollapsed((c) => ({ ...c, [parentId]: !c[parentId] })),
    [],
  );

  const orderedTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    const byParent = new Map();
    const top = [];
    for (const t of tasks) {
      if (t.parent_id) {
        if (!byParent.has(t.parent_id)) byParent.set(t.parent_id, []);
        byParent.get(t.parent_id).push(t);
      } else {
        top.push(t);
      }
    }
    top.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    byParent.forEach((arr) => arr.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
    const out = [];
    for (const p of top) {
      out.push(p);
      const kids = byParent.get(p.id) || [];
      for (const k of kids) out.push(k);
      byParent.delete(p.id);
    }
    // Orphans (subtasks whose parent was deleted)
    byParent.forEach((arr) => arr.forEach((k) => out.push(k)));
    return out;
  }, [tasks]);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      if (t.parent_id) {
        if (!map.has(t.parent_id)) map.set(t.parent_id, []);
        map.get(t.parent_id).push(t);
      }
    }
    return map;
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const hasFilter = q || workstreamFilter || statusFilter;
    if (!hasFilter) {
      return orderedTasks.filter((t) => !(t.parent_id && collapsed[t.parent_id]));
    }
    const matches = (t) => {
      if (q && !(t.task || "").toLowerCase().includes(q) && !(t.subtask || "").toLowerCase().includes(q)) return false;
      if (workstreamFilter && t.workstream !== workstreamFilter) return false;
      if (statusFilter && t.status !== statusFilter) return false;
      return true;
    };
    const keep = new Set();
    for (const t of tasks) {
      if (!matches(t)) continue;
      keep.add(t.id);
      if (t.parent_id) keep.add(t.parent_id);
      const kids = childrenByParent.get(t.id) || [];
      for (const k of kids) keep.add(k.id);
    }
    return orderedTasks.filter(
      (t) => keep.has(t.id) && !(t.parent_id && collapsed[t.parent_id]),
    );
  }, [tasks, orderedTasks, childrenByParent, search, workstreamFilter, statusFilter, collapsed]);

  return {
    orderedTasks,
    childrenByParent,
    filteredTasks,
    collapsed,
    toggleCollapse,
    setCollapsed,
  };
}
